import { createSlice, PayloadAction } from "@reduxjs/toolkit"

import Question from "../../models/question"
import { getUserLibrary, getQuestion, generateTitle } from "../api/question-api"
import { askQuestion } from "../api/qna-api"

interface InitialState {
    previousQuestions: undefined | Question[]
    currentQuestion: undefined | Question
    answer: undefined | Question.Answer
    library: undefined | Question.GetLibrary.Response
    lastLibraryFetch: number
    askStatus: "idle" | "pending" | "fulfilled" | "rejected"
    questionAsking: undefined | string
    fetchingQuestion: boolean
    fetchingLibrary: boolean
}

const initialState: InitialState = {
    previousQuestions: undefined,
    currentQuestion: undefined,
    answer: undefined,
    library: undefined,
    lastLibraryFetch: 0,
    askStatus: "idle",
    questionAsking: undefined,
    fetchingQuestion: false,
    fetchingLibrary: false,
}

export const questionSlice = createSlice({
    name: "question",
    initialState,
    reducers: {
        appendAnswerText: (state, action) => {
            if (!state.answer) return
            state.answer.answer.summary += action.payload
        },
        addAnswerSources: (state, action) => {
            if (!state.answer) return
            state.answer.answer = action.payload
        },
        clearQuestion: () => initialState,
        clearAnswer: (state) => {
            state.askStatus = "idle"
            state.answer = undefined
        },
        createEmptyAnswer: (
            state,
            action: PayloadAction<string | { question: string; clientRequestId: string }>,
        ) => {
            const question =
                typeof action.payload === "string" ? action.payload : action.payload.question
            state.askStatus = "fulfilled"
            state.lastLibraryFetch = 0
            state.answer = {
                question,
                clientRequestId:
                    typeof action.payload === "string" ? undefined : action.payload.clientRequestId,
                answer: {
                    sources: [],
                    summary: "",
                },
            }
        },
        attachSavedAnswerId: (
            state,
            action: PayloadAction<{ id: string; clientRequestId: string }>,
        ) => {
            if (!state.answer || state.answer.clientRequestId !== action.payload.clientRequestId)
                return
            state.answer.id = action.payload.id
        },
        clearQuestionAsking: (state) => {
            state.questionAsking = undefined
        },
        openError: (state) => {
            state.askStatus = "rejected"
            state.questionAsking = undefined
        },
        addNewQuestion: (state, action: PayloadAction<Question>) => {
            const newQuestion = action.payload
            if (state.previousQuestions) {
                state.previousQuestions = [newQuestion, ...state.previousQuestions].slice(0, 8)
            } else {
                state.previousQuestions = [newQuestion]
            }
            if (state.library) {
                state.library = [newQuestion, ...state.library]
            }
        },
    },
    extraReducers: (builder) =>
        builder
            .addCase(askQuestion.pending, (state, action) => {
                state.askStatus = "pending"
                state.questionAsking = action.meta.arg.question
            })
            .addCase(askQuestion.rejected, (state) => {
                state.askStatus = "rejected"
                state.questionAsking = undefined
            })

            .addCase(getQuestion.pending, (state) => {
                state.fetchingQuestion = true
                state.currentQuestion = undefined
            })
            .addCase(getQuestion.fulfilled, (state, action) => {
                state.fetchingQuestion = false
                if (action.payload.status !== 200) return
                state.currentQuestion = action.payload.data
            })
            .addCase(getQuestion.rejected, (state) => {
                state.fetchingQuestion = false
            })

            .addCase(getUserLibrary.pending, (state) => {
                state.fetchingLibrary = true
            })
            .addCase(getUserLibrary.fulfilled, (state, action) => {
                state.fetchingLibrary = false
                if (action.payload.status !== 200) return
                state.library = action.payload.data ?? []
                state.lastLibraryFetch = Date.now() + 60_000
                state.previousQuestions = action.payload.data?.slice(0, 8) ?? []
            })
            .addCase(getUserLibrary.rejected, (state) => {
                state.fetchingLibrary = false
            })

            .addCase(generateTitle.fulfilled, (state, action) => {
                if (action.payload.status !== 200) return
                const title = action.payload.data?.title
                if (!title) return
                const questionId = action.meta.arg.questionId
                if (state.previousQuestions) {
                    state.previousQuestions = state.previousQuestions.map((q) =>
                        q.id === questionId ? { ...q, title } : q,
                    )
                }
                if (state.library) {
                    state.library = state.library.map((q) =>
                        q.id === questionId ? { ...q, title } : q,
                    )
                }
            }),
})

export const {
    appendAnswerText,
    addAnswerSources,
    clearQuestion,
    clearAnswer,
    createEmptyAnswer,
    attachSavedAnswerId,
    clearQuestionAsking,
    openError,
    addNewQuestion,
} = questionSlice.actions
export default questionSlice.reducer
